import React, { useState } from 'react';
import {
  Typography,
  useTheme,
  Avatar,
  List,
  ListItemButton,
  IconButton,
  Button,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
} from '@mui/material';
import ArticleRoundedIcon from '@mui/icons-material/ArticleRounded';
import { validateAuth, getApiBaseUrl } from '../util/util';
import { nameToColour, getInitials, timeSinceEvent, fetchData } from '../util/util';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import axios from 'axios';
import moment from 'moment';
import { useSelector, useDispatch } from 'react-redux';
import { userTypeSelector, securityGroupSelector } from '../redux/selectors';
import { notificationSuccess, notificationError } from '../redux/actions/app';
import { useHistory } from 'react-router-dom';
import FallbackPage from '../containers/FallbackPage';

const STATUS_TO_COLOR = {
  completed: 'lightsuccess',
  scheduled: 'lightwarning',
  cancelled: 'lighterror',
  rescheduled: 'lightwarning',
  incomplete: 'lighterror',
};

const NotificationsWidget = (props) => {
  const { data, show, type, sidebarExpanded, setShowNotifications, setRefreshNotifications } =
    props;
  const theme = useTheme();
  const currentUserType = useSelector(userTypeSelector);
  const currentSelectedSecurityGroup = useSelector(securityGroupSelector);
  const dispatch = useDispatch();
  const history = useHistory();

  const [showMaintenanceDialog, setShowMaintenanceDialog] = useState(false);
  const [currentMaintenance, setCurrentMaintenance] = useState(null);

  const callAPI = (method = 'put', params = {}, path) => {
    return validateAuth(async (session) => {
      try {
        const response = await axios({
          method: method,
          url: `${getApiBaseUrl(currentUserType.type)}${path}`,
          params: params,
          headers: {
            Authorization: session.getIdToken().getJwtToken(),
          },
          timeout: 15 * 1000,
        });
        return response;
      } catch (error) {
        return error;
      }
    });
  };

  const fetchMaintenanceDetails = (notification) => {
    fetchData(
      'get',
      `/maintenance/${notification.ticket_number}`,
      {},
      (responseData) => {
        const data = typeof responseData === 'string' ? JSON.parse(responseData) : responseData;

        const maint = {
          ...data,
          scheduled_date: data.scheduled_date ? moment.utc(data.scheduled_date) : null,
        };

        setCurrentMaintenance(maint);
        setShowMaintenanceDialog(true);
      },
      currentUserType.type,
      () => {
        dispatch(notificationError('Failed to fetch maintenance details.'));
      }
    );
  };

  const sidebarStyle = {
    position: 'fixed',
    left: sidebarExpanded ? `calc(440px + 16px)` : 'calc(72px + 16px)',
    bottom: theme.spacing(2),
    zIndex: 1202,
    width: '380px',
    padding: theme.spacing(2),
    height: '512px',
    borderRadius: theme.shape.borderRadius * 2,
    boxShadow: '0px 2px 16px rgba(0, 0, 0, 0.1)',
  };

  const dashboardStyle = {
    width: 'calc(100% - 64px - 2px)',
    padding: theme.spacing(4),
    height: '100%',
    borderRadius: theme.shape.borderRadius * 2,
  };

  const handleIsUnreadSave = async (notification) => {
    const response = await callAPI(
      'put',
      {
        isUnread: false,
      },
      `/web_push_notifications/${notification.id}`
    );
    if (response.status === 200) {
      console.log('Notification isUnread updated successfully.');

      const isAalNotification =
        notification.request_type === 'aal' || notification.request_type === 'aal_approval';

      const customerId =
        notification.customer_id || notification.customerId || notification.customerID;

      const datacentreId =
        notification.datacentre_id || notification.datacentreId || notification.dataCentreId;

      if (notification.request_type === 'maintenance') {
        fetchMaintenanceDetails(notification);
      } else if (isAalNotification) {
        const queryParams = new URLSearchParams();

        if (customerId) queryParams.set('customer_id', customerId);
        if (datacentreId) queryParams.set('datacentre_id', datacentreId);
        if (notification.ticket_number) queryParams.set('approval_id', notification.ticket_number);

        history.push({
          pathname: '/a-authorised-access-list',
          search: queryParams.toString() ? `?${queryParams.toString()}` : '',
        });
      } else if (notification.request_type === 'notification') {
        history.push(
          `/${
            currentUserType.type === 'external' ||
            currentSelectedSecurityGroup === 'ADMIN' ||
            currentSelectedSecurityGroup === 'SUPERVISOR'
              ? 'a'
              : currentSelectedSecurityGroup === 'SECURITY_TEAM_USER'
                ? 's'
                : 'o'
          }-notifications`
        );
      } else {
        history.push(
          `/${
            currentUserType.type === 'external' ||
            currentSelectedSecurityGroup === 'ADMIN' ||
            currentSelectedSecurityGroup === 'SUPERVISOR'
              ? 'a'
              : currentSelectedSecurityGroup === 'SECURITY_TEAM_USER'
                ? 's'
                : 'o'
          }-${notification.request_type}-requests/${notification.ticket_number}`
        );
      }
      setRefreshNotifications(true);
      if (notification.request_type !== 'maintenance') {
        setShowNotifications(false);
      }
    } else {
      dispatch(notificationError('Failed to save comment.'));
    }
  };

  const markAllNotificationsAsRead = async () => {
    const response = await callAPI(
      'post',
      { notificationContact: currentUserType.user.email },
      `/web_push_notifications/mark_all_as_read`
    );
    if (response.status === 200) {
      console.log('All notifications marked as read successfully.');
      setRefreshNotifications(true);
    } else {
      dispatch(notificationError('Failed to mark all notifications as read.'));
    }
  };

  const [warningDialog, setWarningDialog] = useState(false);

  const openWarning = () => setWarningDialog(true);
  const closeWarning = () => setWarningDialog(false);

  const clearAllNotifications = async () => {
    setRefreshNotifications(false);
    const response = await callAPI(
      'post',
      { notificationContact: currentUserType.user.email },
      `/web_push_notifications/clear_all`
    );
    if (response.status === 200) {
      console.log('All notifications cleared successfully.');
      setRefreshNotifications(true);
      closeWarning();
    } else {
      dispatch(notificationError('Failed to clear all notifications.'));
      closeWarning();
    }
  };

  return (
    <div
      className="col gap"
      style={{
        ...(type === 'SIDEBAR' ? sidebarStyle : dashboardStyle),
        backgroundColor: theme.palette.background.default,
        border: `1px solid ${theme.palette.divider}`,
        overflowY: 'auto',
        transition: 'left .25s ease-in-out, opacity .15s ease-in-out',
        opacity: show || type === 'DASHBOARD' ? '100%' : '0%',
        pointerEvents: show || type === 'DASHBOARD' ? 'all' : 'none',
      }}
    >
      <Dialog
        open={showMaintenanceDialog && currentMaintenance !== null}
        onClose={() => setShowMaintenanceDialog(false)}
      >
        {currentMaintenance && (
          <>
            <DialogTitle>
              <div className="row justify-between align-center">
                <Typography variant="h6">{currentMaintenance.maintenance_name}</Typography>
                <Chip
                  color={STATUS_TO_COLOR[currentMaintenance.status.toLowerCase()]}
                  label={currentMaintenance.status}
                  sx={{ width: 'min-content', marginLeft: 1 }}
                />
              </div>
            </DialogTitle>
            <DialogContent>
              <Typography variant="overline">{currentMaintenance.datacentre_name}</Typography>
              <Typography
                variant="body2"
                sx={{ marginBottom: 2 }}
              >{`${currentMaintenance.scheduled_date.clone().format('D MMM h:mm a')} - ${currentMaintenance.scheduled_date.clone().add(currentMaintenance.duration, 'hours').format('D MMM h:mm a')}`}</Typography>
              <Typography variant="body2">{currentMaintenance.description}</Typography>
              <Typography variant="body2" sx={{ marginBottom: 2, marginTop: 1 }}>
                {currentMaintenance.reason}
              </Typography>
              <div className="row pad-lg">
                <div className="col" style={{ width: '50%' }}>
                  <Typography variant="overline">Category</Typography>
                  <Typography variant="body2" sx={{ marginLeft: 1, marginBottom: 2 }}>
                    {currentMaintenance.category}
                  </Typography>
                  <Typography variant="overline">Risk</Typography>
                  <Typography variant="body2" sx={{ marginLeft: 1, marginBottom: 2 }}>
                    {currentMaintenance.outage_risk}
                  </Typography>
                  <Typography variant="overline">Redundancy</Typography>
                  <Typography variant="body2" sx={{ marginLeft: 1, marginBottom: 2 }}>
                    {currentMaintenance.resiliency_impact}
                  </Typography>
                </div>
                <div className="col" style={{ width: '50%' }}>
                  <Typography variant="overline">Maintenance Number</Typography>
                  <Typography variant="body2" sx={{ marginLeft: 1, marginBottom: 2 }}>
                    {currentMaintenance.maintenance_reference_number}
                  </Typography>
                  <Typography variant="overline">Reference Number</Typography>
                  <Typography variant="body2" sx={{ marginLeft: 1, marginBottom: 2 }}>
                    {currentMaintenance.job_reference_number}
                  </Typography>
                </div>
              </div>
            </DialogContent>
            <DialogActions>
              <Button
                variant="outlined"
                color="tertiary"
                onClick={() => setShowMaintenanceDialog(false)}
              >
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <div className="row gap align-center">
        <Typography variant="h6" sx={{ marginRight: 'auto' }}>
          {type === 'SIDEBAR' ? 'Notifications' : 'Recent Activity'}
        </Typography>
        {type === 'SIDEBAR' && (
          <Tooltip title="Mark all notifications as read" arrow placement="top">
            <Button
              variant="outlined"
              color="tertiary"
              sx={{
                minWidth: '130px',
                height: '25px',
                fontSize: '0.70rem',
                whiteSpace: 'nowrap',
                padding: '0 8px',
              }}
              onClick={() => markAllNotificationsAsRead()}
            >
              Mark All as Read
            </Button>
          </Tooltip>
        )}
        {type === 'SIDEBAR' && (
          <Tooltip title="Clear all notifications from this list" arrow placement="top">
            <Button
              variant="outlined"
              color="tertiary"
              sx={{
                minWidth: '75px',
                height: '25px',
                fontSize: '0.70rem',
                whiteSpace: 'nowrap',
                padding: '0 8px',
              }}
              onClick={openWarning}
            >
              Clear All
            </Button>
          </Tooltip>
        )}
        {
          <Dialog open={warningDialog} onClose={closeWarning}>
            <DialogContent>
              <DialogContentText>
                Are you sure you want to clear all notifications?
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ display: 'flex', justifyContent: 'space-between', px: 3 }}>
              <Button
                onClick={clearAllNotifications}
                color="tertiary"
                variant="outlined"
                sx={{ minWidth: '150px', height: '48px' }}
              >
                Yes
              </Button>
              <Button
                onClick={closeWarning}
                color="primary"
                variant="contained"
                sx={{ minWidth: '150px', height: '48px' }}
              >
                No
              </Button>
            </DialogActions>
          </Dialog>
        }
        {type === 'SIDEBAR' && (
          <Tooltip title="Close Notifications" arrow placement="top">
            <IconButton color="tertiary" onClick={() => setShowNotifications(false)}>
              <CloseRoundedIcon />
            </IconButton>
          </Tooltip>
        )}
      </div>
      {data.length === 0 && <FallbackPage type="notification" />}
      <List className="col gap">
        {data.map((notification) => (
          <ListItemButton
            key={notification.id}
            className="row gap-lg"
            sx={{
              alignItems: 'start',
              padding: ' 16px',
              borderRadius: 1,
              backgroundColor: notification.is_unread
                ? theme.palette.background.paper
                : 'transparent',
            }}
            onClick={() => {
              handleIsUnreadSave(notification);
            }}
          >
            <Avatar
              sx={{
                bgcolor: `${nameToColour(notification.updated_by)}.contrastText`,
                width: 36,
                height: 36,
                fontSize: 16,
                marginTop: 1,
              }}
            >
              {getInitials(notification.updated_by)}
            </Avatar>
            <div className="col gap width-max">
              <div className="row gap-sm">
                <Typography variant="body2">{notification.title}</Typography>
                {/* Circle accent for unread notifications */}
                {notification.is_unread && (
                  <div
                    style={{
                      minWidth: '8px',
                      height: '8px',
                      backgroundColor: theme.palette.accent4.contrastText,
                      borderRadius: '100%',
                      marginLeft: 'auto',
                    }}
                  />
                )}
              </div>
              <div className="row gap-sm">
                <ArticleRoundedIcon fontSize="10" color="tertiary" sx={{ marginTop: '1px' }} />
                <Typography variant="body2">
                  {notification.request_type === 'notification'
                    ? 'Manual Notification'
                    : `#${notification.ticket_number}`}
                </Typography>
                <Typography variant="caption" sx={{ marginLeft: 'auto' }}>
                  {timeSinceEvent(notification.created_at)}
                </Typography>
              </div>
            </div>
          </ListItemButton>
        ))}
      </List>
    </div>
  );
};

export default NotificationsWidget;
